/**
 * 15-Minute Inactivity Session Lock & RAM Zeroization Hook
 * 
 * Complies with HIPAA Security Rule § 164.312(a)(2)(iii) (Automatic Logoff):
 * "Implement electronic procedures that terminate an electronic session after
 * a predetermined time of inactivity."
 * 
 * Default Inactivity Threshold: 15 minutes (900 seconds / 900,000 ms).
 * 
 * On session timeout:
 * 1. Automatically locks workstation DOM view.
 * 2. Executes zeroization callback to wipe volatile RSA-2048 / AES-256 CryptoKeys and decrypted patient records.
 * 3. Prevents auto-resumption until an authorized PIN or passphrase is provided.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export const HIPAA_DEFAULT_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 900,000 ms = 15 minutes
export const DEFAULT_WARNING_THRESHOLD_MS = 60 * 1000; // 60 seconds warning before lock
export const DEFAULT_UNLOCK_PIN = '1234';

export interface UseInactivityLockOptions {
  /**
   * Timeout in milliseconds before session automatically locks.
   * Defaults to 900,000 ms (15 minutes per HIPAA § 164.312(a)(2)(iii)).
   */
  timeoutMs?: number;

  /**
   * Time in milliseconds before lock to present warning state.
   * Defaults to 60,000 ms (1 minute).
   */
  warningMs?: number;

  /**
   * Zeroization callback invoked immediately when session locks.
   * Used to wipe CryptoKeys, purge decrypted memory buffers, and reset cached state.
   */
  onZeroize?: () => void;

  /**
   * Optional additional callback invoked upon session lock.
   */
  onLock?: () => void;

  /**
   * Authorized PIN or passphrase required to restore session.
   * Defaults to '1234'.
   */
  expectedPin?: string;

  /**
   * Custom validation function for unlock credentials.
   */
  validateUnlock?: (input: string) => boolean;

  /**
   * Whether the inactivity lock is actively monitoring events.
   * Defaults to true.
   */
  enabled?: boolean;
}

export interface UseInactivityLockReturn {
  isLocked: boolean;
  showWarning: boolean;
  secondsRemaining: number;
  lastActiveTimestamp: number;
  failedAttempts: number;
  inactivityTimeoutMs: number;
  unlock: (pinOrPassphrase: string) => boolean;
  lockNow: () => void;
  resetTimer: () => void;
}

/**
 * Pure state controller for deterministic unit testing and headless execution.
 */
export class InactivitySessionController {
  private timeoutMs: number;
  private warningMs: number;
  private onZeroize?: () => void;
  private onLock?: () => void;
  private expectedPin: string;
  private validateUnlock?: (input: string) => boolean;

  private lastActiveTimestamp: number;
  private isLocked: boolean = false;
  private showWarning: boolean = false;
  private failedAttempts: number = 0;
  private zeroized: boolean = false;

  constructor(options: UseInactivityLockOptions = {}) {
    this.timeoutMs = options.timeoutMs ?? HIPAA_DEFAULT_INACTIVITY_TIMEOUT_MS;
    this.warningMs = options.warningMs ?? DEFAULT_WARNING_THRESHOLD_MS;
    this.onZeroize = options.onZeroize;
    this.onLock = options.onLock;
    this.expectedPin = options.expectedPin ?? DEFAULT_UNLOCK_PIN;
    this.validateUnlock = options.validateUnlock;
    this.lastActiveTimestamp = Date.now();
  }

  recordActivity(now: number = Date.now()): void {
    if (this.isLocked) {
      // Activity during locked state does NOT unlock or extend session
      return;
    }
    this.lastActiveTimestamp = now;
    this.showWarning = false;
  }

  checkInactivity(now: number = Date.now()): {
    isLocked: boolean;
    showWarning: boolean;
    secondsRemaining: number;
  } {
    if (this.isLocked) {
      return {
        isLocked: true,
        showWarning: false,
        secondsRemaining: 0,
      };
    }

    const elapsed = now - this.lastActiveTimestamp;
    const remaining = Math.max(0, this.timeoutMs - elapsed);
    const secondsRemaining = Math.ceil(remaining / 1000);

    if (elapsed >= this.timeoutMs) {
      this.lockSession();
      return {
        isLocked: true,
        showWarning: false,
        secondsRemaining: 0,
      };
    }

    if (remaining <= this.warningMs) {
      this.showWarning = true;
    } else {
      this.showWarning = false;
    }

    return {
      isLocked: this.isLocked,
      showWarning: this.showWarning,
      secondsRemaining,
    };
  }

  lockSession(): void {
    if (!this.isLocked) {
      this.isLocked = true;
      this.showWarning = false;
      this.zeroized = true;
      if (this.onZeroize) {
        this.onZeroize();
      }
      if (this.onLock) {
        this.onLock();
      }
    }
  }

  unlock(pinOrPassphrase: string, now: number = Date.now()): boolean {
    const isValid = this.validateUnlock
      ? this.validateUnlock(pinOrPassphrase)
      : pinOrPassphrase.trim() === this.expectedPin;

    if (isValid) {
      this.isLocked = false;
      this.showWarning = false;
      this.failedAttempts = 0;
      this.zeroized = false;
      this.lastActiveTimestamp = now;
      return true;
    }

    this.failedAttempts += 1;
    return false;
  }

  // Getters
  getIsLocked(): boolean { return this.isLocked; }
  getShowWarning(): boolean { return this.showWarning; }
  getLastActiveTimestamp(): number { return this.lastActiveTimestamp; }
  getFailedAttempts(): number { return this.failedAttempts; }
  getTimeoutMs(): number { return this.timeoutMs; }
  isZeroized(): boolean { return this.zeroized; }
}

/**
 * React hook managing inactivity lifecycle, window listeners, and RAM zeroization.
 */
export function useInactivityLock(options: UseInactivityLockOptions = {}): UseInactivityLockReturn {
  const {
    timeoutMs = HIPAA_DEFAULT_INACTIVITY_TIMEOUT_MS,
    warningMs = DEFAULT_WARNING_THRESHOLD_MS,
    onZeroize,
    onLock,
    expectedPin = DEFAULT_UNLOCK_PIN,
    validateUnlock,
    enabled = true,
  } = options;

  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(Math.ceil(timeoutMs / 1000));
  const [failedAttempts, setFailedAttempts] = useState<number>(0);
  const [lastActiveTimestamp, setLastActiveTimestamp] = useState<number>(() => Date.now());

  const controllerRef = useRef<InactivitySessionController>(
    new InactivitySessionController({
      timeoutMs,
      warningMs,
      onZeroize,
      onLock,
      expectedPin,
      validateUnlock,
    })
  );

  // Sync callbacks and config on controller
  useEffect(() => {
    controllerRef.current = new InactivitySessionController({
      timeoutMs,
      warningMs,
      onZeroize,
      onLock,
      expectedPin,
      validateUnlock,
    });
  }, [timeoutMs, warningMs, onZeroize, onLock, expectedPin, validateUnlock]);

  const lockNow = useCallback(() => {
    controllerRef.current.lockSession();
    setIsLocked(true);
    setShowWarning(false);
    setSecondsRemaining(0);
  }, []);

  const unlock = useCallback((pinOrPassphrase: string) => {
    const success = controllerRef.current.unlock(pinOrPassphrase);
    if (success) {
      setIsLocked(false);
      setShowWarning(false);
      setFailedAttempts(0);
      setLastActiveTimestamp(controllerRef.current.getLastActiveTimestamp());
      setSecondsRemaining(Math.ceil(timeoutMs / 1000));
      return true;
    } else {
      setFailedAttempts(controllerRef.current.getFailedAttempts());
      return false;
    }
  }, [timeoutMs]);

  const resetTimer = useCallback(() => {
    if (!controllerRef.current.getIsLocked()) {
      controllerRef.current.recordActivity();
      setLastActiveTimestamp(controllerRef.current.getLastActiveTimestamp());
      setShowWarning(false);
      setSecondsRemaining(Math.ceil(timeoutMs / 1000));
    }
  }, [timeoutMs]);

  // Window event listeners to track user presence
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    let throttleTimer: any = null;

    const handleUserActivity = () => {
      if (controllerRef.current.getIsLocked()) return;

      // Throttle state updates to avoid unnecessary React re-renders on rapid mousemove
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          throttleTimer = null;
          controllerRef.current.recordActivity();
          setLastActiveTimestamp(controllerRef.current.getLastActiveTimestamp());
        }, 300);
      }
    };

    const monitoredEvents = [
      'mousemove',
      'keydown',
      'touchstart',
      'scroll',
      'pointerdown',
      'mousedown',
    ];

    monitoredEvents.forEach((evt) => {
      window.addEventListener(evt, handleUserActivity, { passive: true });
    });

    return () => {
      monitoredEvents.forEach((evt) => {
        window.removeEventListener(evt, handleUserActivity);
      });
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [enabled]);

  // Periodic heartbeat interval to evaluate idle duration
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      const status = controllerRef.current.checkInactivity(Date.now());
      setIsLocked(status.isLocked);
      setShowWarning(status.showWarning);
      setSecondsRemaining(status.secondsRemaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [enabled]);

  return {
    isLocked,
    showWarning,
    secondsRemaining,
    lastActiveTimestamp,
    failedAttempts,
    inactivityTimeoutMs: timeoutMs,
    unlock,
    lockNow,
    resetTimer,
  };
}
